import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import React from 'react';
import { toast } from 'sonner';
import { useErrorMessages } from '../../../../aida/hooks';
import { useImageAttachmentMutation } from '../../../../queries';
import { Button } from '../../Button';
import { Checkbox } from '../../Checkbox';
import { styleSheet } from './styles';

interface SelectImageTabProps {
	allowMultipleSelect?: boolean;
	imagesForSelection?: { imagePath: string; slug: string }[];
	onSelectImages?: (images: { slug: string; fileAttachment: Api.IFileAttachment }[]) => void;
	onPreProcessSelectImage?: (file: File, context: { imagePath: string; slug: string }) => File | Promise<File>;
	title?: string;
	subtitle?: string;
	onOpenChange: (open: boolean) => void;
	onEditWithAI: (image: Api.IFileAttachment) => void;
}

export function SelectImageTab({
	onSelectImages,
	onPreProcessSelectImage,
	allowMultipleSelect,
	imagesForSelection,
	title = 'Select an image',
	subtitle = 'Select an image from the list of images',
	onOpenChange,
	onEditWithAI,
}: SelectImageTabProps) {
	const [selectedImages, setSelectedImages] = React.useState<{ imagePath: string; slug: string }[]>([]);
	const errorMessages = useErrorMessages();
	const [generatingFiles, setGeneratingFiles] = React.useState<boolean>(false);
	const [progress, setProgress] = React.useState<number>(0);

	// Mutations
	const { mutateAsync: uploadImage } = useImageAttachmentMutation({
		onError: error => {
			errorMessages.pushApiError(error);
		},
	});

	// Handlers
	const generateImage = async (): Promise<{ slug: string; fileAttachment: Api.IFileAttachment }[]> => {
		if (selectedImages.length === 0) {
			return [];
		}

		setGeneratingFiles(true);
		setProgress(0);

		const generatedFiles = [];

		try {
			for (let i = 0; i < selectedImages.length; i++) {
				const image = selectedImages[i];
				const response = await fetch(image.imagePath);
				const blob = await response.blob();
				let fileToUpload = new File([blob], `selected-image-${Date.now()}-${image.slug}.png`, {
					type: 'image/png',
				});

				if (onPreProcessSelectImage) {
					fileToUpload = await Promise.resolve(
						onPreProcessSelectImage(fileToUpload, { imagePath: image.imagePath, slug: image.slug })
					);
				}

				const fileAttachment = await uploadImage({
					files: [fileToUpload],
					resizeForSocialMedia: true,
				});

				generatedFiles.push({ slug: image.slug, fileAttachment: fileAttachment[0] });

				setProgress(i + 1);

				return generatedFiles;
			}
		} finally {
			setGeneratingFiles(false);
		}
	};

	const handleEditWithAI = async () => {
		if (selectedImages.length === 0 || selectedImages.length > 1) {
			toast.error('Please select only one image to edit with AI');
			return;
		}

		const generatedFiles = await generateImage();
		if (generatedFiles.length === 0) {
			return;
		}

		const image = generatedFiles[0];
		onEditWithAI(image.fileAttachment);
	};

	const saveImages = async () => {
		const generatedFiles = await generateImage();
		onSelectImages?.(generatedFiles);
	};

	return (
		<div className={css(styleSheet.selectImageTabContainer)}>
			<div>
				<h1>{title}</h1>
				<p>{subtitle}</p>
			</div>

			<div className={css(styleSheet.imagesRow)}>
				{imagesForSelection?.map(image => (
					<div key={image.slug} className={css(styleSheet.imageContainer)}>
						<img src={image.imagePath} alt={image.slug} className={css(styleSheet.image)} />

						<label className={css(styleSheet.checkboxContainer)}>
							<Checkbox
								checked={selectedImages.some(i => i.slug === image.slug)}
								onCheckedChange={() => {
									if (allowMultipleSelect) {
										if (selectedImages.includes(image)) {
											setSelectedImages(selectedImages.filter(i => i.slug !== image.slug));
										} else {
											setSelectedImages([...selectedImages, image]);
										}

										return;
									}

									if (selectedImages.includes(image)) {
										setSelectedImages(selectedImages.filter(i => i.slug !== image.slug));
									} else {
										setSelectedImages([image]);
									}
								}}
							/>
							<span className={css(styleSheet.checkboxLabel)}>Select Image</span>
						</label>
					</div>
				))}
			</div>

			<div className={css(styleSheet.modalActionsContainer)}>
				<Button variant='primary' onClick={() => saveImages()} isLoading={generatingFiles}>
					{generatingFiles ? `Generating ${progress} of ${selectedImages.length} images...` : 'Save'}
				</Button>
				<Button
					variant='secondary'
					onClick={() => handleEditWithAI()}
					disabled={selectedImages.length === 0 || selectedImages.length > 1}
					isLoading={generatingFiles}
				>
					Edit with AI
				</Button>
				<Button variant='secondary' onClick={() => onOpenChange(false)}>
					Cancel
				</Button>
			</div>
		</div>
	);
}
